import { FC, useState } from "react";
import quotationImg from "images/quotation.png";
import quotationImg2 from "images/quotation2.png";

import { useData } from "data/data-provider";
import { isUsableUrl } from "utils/isUsableUrl";

// Gold 5-star rating glyph. Testimonials are curated, so every card shows a
// full 5-star row (same convention as Design B's testimonial cards).
const CustomStar: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" aria-hidden="true">
    <path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      fill="#FFC107"
      stroke="#E8A902"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Small "verified" check beside the reviewer name — a subtle credibility cue.
const VerifiedIcon: FC<{ className?: string }> = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

export interface SectionClientSayProps {
  className?: string;
  uniqueClassName: string;
}

const SectionTestimonials: FC<SectionClientSayProps> = ({
  className = "",
  uniqueClassName = "",
}) => {
  const { testimonials } = useData();

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "grid"     → static multi-column grid, every testimonial visible at once
  //                (matches the FMS picker's "3 cards side by side" thumbnail)
  //   "carousel" → JS-driven one-at-a-time slider (prev/next + clickable dots)
  // Default "grid". Previously the grid variant was a Glide slider (perView:1)
  // which rendered one card at a time — visually indistinguishable from the
  // carousel (1NOW-1246). It now renders a true static grid.
  const layoutVariant: "grid" | "carousel" =
    testimonials?.layout_variant === "carousel" ? "carousel" : "grid";
  const items: any[] = testimonials?.keypoints ?? [];

  // Carousel state — JS-driven `translateX` instead of a native overflow-x
  // scrollbar so the browser horizontal bar never leaks through.
  const [currentIndex, setCurrentIndex] = useState(0);
  const goTo = (i: number) =>
    setCurrentIndex(Math.max(0, Math.min(items.length - 1, i)));

  return (
    <div
      className={`nc-SectionClientSay relative ${className} `}
      data-nc-id="SectionClientSay"
    >
      <div
        className={`nc-Section-Heading relative flex flex-col sm:flex-row sm:items-end justify-between ${className}`}
      >
        <div className={"text-center w-full max-w-2xl mx-auto"}>
          <span className={`leading-none text-2xl font-semibold`}>
            <span className="text-text-color" dangerouslySetInnerHTML={{ __html: testimonials?.heading }} />
          </span>
        </div>
      </div>

      {/* Grid layout — static multi-column grid, every testimonial visible at
          once. Each item is a premium review card: a 5-star row, the quote,
          then an avatar + name + "Verified Renter" footer split off by a
          divider. Shadow + hover-lift give it depth so it reads as a polished
          card, not a plain bordered box — and distinct from the single-card
          carousel below. */}
      {layoutVariant === "grid" && items.length > 0 && (
        <div className="mt-12 lg:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {items.map((item: any, index: any) => (
            <div
              key={index}
              className="flex flex-col rounded-2xl border border-secondary-100 bg-card-color p-6 sm:p-7 text-left shadow-lg shadow-secondary-100/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              {/* 5-star rating row */}
              <div className="flex items-center gap-0.5 mb-4">
                {[...Array(5)].map((_, i) => (
                  <CustomStar key={i} />
                ))}
              </div>

              {/* Quote — flex-1 so multi-line quotes push the author footer to
                  the bottom and every card in a row stays the same height. */}
              <p className="text-base text-secondary-text-color leading-relaxed mb-6 flex-1 [&_p]:m-0 [&_p]:inline">
                <span dangerouslySetInnerHTML={{ __html: item?.description }} />
              </p>

              {/* Author footer — avatar + name + verified check, separated from
                  the quote by a hairline divider. */}
              <div className="mt-auto flex items-center gap-3 border-t border-secondary-100 pt-4">
                {isUsableUrl(item?.image_url) && (
                  <img
                    src={item.image_url}
                    alt=""
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                    className="w-11 h-11 shrink-0 rounded-full object-cover ring-2 ring-primary-200/30"
                  />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-text-color [&_p]:m-0 [&_p]:inline">
                      <span dangerouslySetInnerHTML={{ __html: item?.heading }} />
                    </span>
                    <VerifiedIcon className="h-4 w-4 shrink-0 text-primary-200" />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-secondary-text-color mt-0.5">
                    Verified Renter
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Carousel layout — JS-driven one-at-a-time slider. Reuses Design A's
          OWN testimonial card markup/classes (avatar + description + heading)
          so each slide matches the grid styling; only the navigation
          mechanism differs (transform:translateX + prev/next + clickable
          dots). */}
      {layoutVariant === "carousel" && items.length > 0 && (
        <div className="relative md:mb-16 max-w-2xl mx-auto">
          <div className="mt-12 lg:mt-16 relative">
            <img
              className="opacity-50 md:opacity-100 absolute -mr-16 lg:mr-3 right-full top-1"
              src={quotationImg}
              alt=""
            />
            <img
              className="opacity-50 md:opacity-100 absolute -ml-16 lg:ml-3 left-full top-1"
              src={quotationImg2}
              alt=""
            />
            {/* Slide track. transform:translateX shifts whichever slide is
                current into view; transition gives a smooth glide. */}
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {items.map((item: any, index: any) => (
                  <div
                    key={index}
                    className="shrink-0 w-full flex flex-col items-center text-center"
                  >
                    {isUsableUrl(item?.image_url) && (
                      <img
                        src={item.image_url}
                        alt=""
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display =
                            "none";
                        }}
                        className="w-24 h-24 rounded-full mb-4"
                      />
                    )}

                    <span className="block leading-none text-2xl text-secondary-text-color">
                      <span
                        dangerouslySetInnerHTML={{ __html: item?.description }}
                      />
                    </span>
                    <span
                      className={`block ${
                        item.image_url ? "mt-8" : ""
                      } leading-none font-semibold text-2xl text-secondary-text-color`}
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: item?.heading }}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Nav controls — prev/next arrows + clickable dots. Only shown
                when there's more than one testimonial. */}
            {items.length > 1 && (
              <div className="mt-10 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => goTo(currentIndex - 1)}
                  disabled={currentIndex === 0}
                  aria-label="Previous testimonial"
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md focus:outline-none ${
                    currentIndex === 0
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:shadow-lg active:scale-95"
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>

                <div className="flex items-center">
                  {items.map((item: any, index: any) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => goTo(index)}
                      aria-label={`Go to testimonial ${index + 1}`}
                      className={`w-2 h-2 rounded-full mx-1 focus:outline-none transition-colors ${
                        index === currentIndex
                          ? "bg-neutral-500"
                          : "bg-neutral-300"
                      }`}
                    ></button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => goTo(currentIndex + 1)}
                  disabled={currentIndex === items.length - 1}
                  aria-label="Next testimonial"
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md focus:outline-none ${
                    currentIndex === items.length - 1
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:shadow-lg active:scale-95"
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionTestimonials;
